export type VNode = { tag: Tag; props: Props; children?: VNode[] } | string | null | undefined | VNode[];
export type Props = (Record<string, any> & { children?: VNode[] }) | null | undefined;
export type Tag = string | FunctionComponent;
export type FunctionComponent = (props: Props) => VNode;

export function h(tag: Tag, props: Props, ...children: VNode[]): VNode {
  const mergedChildren = children?.length ? children : props?.children;
  return { tag, props: { ...props, children: mergedChildren } };
}

export const Fragment = '_fragment';
const HTML_NS = 'http://www.w3.org/1999/xhtml';
const SVG_NS = 'http://www.w3.org/2000/svg';

export function renderToDom(vnode: VNode, ns: string = HTML_NS): Node | null {
  if (typeof vnode === 'string') return document.createTextNode(escapeHTML(vnode));
  if (typeof vnode === 'number') return document.createTextNode(String(vnode));
  if (typeof vnode === 'boolean') return null;
  if (vnode === null || vnode === undefined) return null;
  if (typeof vnode !== 'object') throw new Error(`Unknown type for vnode: ${typeof vnode}`);

  // Handle array.
  if (Array.isArray(vnode)) return renderToDomFragment(vnode, ns);

  const { tag, props } = vnode;

  // Handle fragment.
  if (tag === Fragment) return renderToDomFragment(props?.children ?? [], ns);

  // Handle native html tags.
  if (typeof tag === 'string') {
    // Change namespace for SVG.
    ns = tag === 'svg' ? SVG_NS : ns;

    // Change namespace back to HTML for children of foreignObject
    const childrenNs = tag === 'foreignObject' ? HTML_NS : ns;

    const el = document.createElementNS(ns, tag);
    for (const [key, value] of Object.entries(props ?? {})) {
      setPropOnElement(el, key, value);
    }

    el.appendChild(renderToDomFragment(props?.children ?? [], childrenNs));
    return el;
  }

  // Handle function components.
  if (typeof tag === 'function') {
    return renderToDom(tag(props), ns);
  }

  // Handle error.
  throw new Error(`Unknown tag of type ${typeof tag}`);
}

function renderToDomFragment(vnodes: VNode[], ns: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  vnodes.map(n => renderToDom(n, ns)).forEach(e => e && fragment.appendChild(e));
  return fragment;
}

function setPropOnElement(el: Element, key: string, value: unknown) {
  if (key === 'children') {
    return;
  } else if (key === 'className') {
    el.setAttribute('class', String(value));
  } else if (key === 'style' && typeof value === 'object') {
    Object.assign((el as HTMLElement).style, value ?? {});
  } else if (value === true) {
    el.setAttribute(key, '');
  } else {
    el.setAttribute(key, String(value));
  }
}

export function renderToString(vnode: VNode): string {
  if (typeof vnode === 'string') return escapeHTML(vnode);
  if (typeof vnode === 'number') return String(vnode);
  if (typeof vnode === 'boolean') return '';
  if (vnode === null || vnode === undefined) return '';
  if (typeof vnode !== 'object') throw new Error(`Unknown type for vnode: ${typeof vnode}`);

  // Handle array.
  if (Array.isArray(vnode)) return renderToStringFragment(vnode);

  const { tag, props } = vnode;

  // Handle fragment.
  if (tag === Fragment) return renderToStringFragment(props?.children ?? []);

  // Handle native html tags.
  if (typeof tag === 'string') {
    const attrsStr = renderAttrsToString(props ?? {});
    const childrenStr = renderToStringFragment(props?.children ?? []);
    const openning = `<${tag}${attrsStr ? ' ' : ''}${attrsStr}>`;
    return selfClosing.includes(tag) ? openning : `${openning}${childrenStr ? '\n' + childrenStr + '\n' : ''}</${tag}>`;
  }

  // Handle function components.
  if (typeof tag === 'function') {
    return renderToString(tag(props));
  }

  // Handle error.
  throw new Error(`Unknown tag of type ${typeof tag}`);
}

function renderToStringFragment(vnodes: VNode[]): string {
  return vnodes.map(renderToString).join('\n');
}

function renderAttrsToString(attrs: object): string {
  return Object.entries(attrs).map(renderAttrEntryToString).filter(Boolean).join(' ');
}

function renderAttrEntryToString([key, value]: [string, any]): string {
  if (key === 'children') {
    return '';
  } else if (key === 'className') {
    return `class="${escapeHTML(value)}"`;
  } else if (key === 'style' && typeof value === 'object') {
    return `style=${escapeHTML(renderStyleToStringUnsafe(value))}`;
  } else if (value === true) {
    return `${escapeHTML(key)}`;
  } else {
    return `${escapeHTML(key)}="${escapeHTML(value)}"`;
  }
}

function renderStyleToStringUnsafe(style: Record<string, string | number | null | undefined>): string {
  return Object.entries(style)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${camelToKebab(key)}:${String(value)}`)
    .join(';');
}

function camelToKebab(s: string): string {
  return s.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`);
}

const selfClosing = [
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
];

const htmlEscapesMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
} as const;

export function escapeHTML(str: any): string {
  return String(str).replace(/[&<>"']/g, tag => htmlEscapesMap[tag as keyof typeof htmlEscapesMap]);
}

export function cn(...args: any[]) {
  return args.filter(Boolean).join(' ');
}
